<template>
  <div>
    <!-- Trigger im Dock (P20): Badge zeigt die Zahl aktiver Filter, wie im Lieferplan -->
    <button class="ret-dock__trigger" :class="{ 'ret-dock__trigger--active': activeFilterCount > 0 }" :title="$t('returns.filterDrawer.title')" @click.stop="open = !open">
      <md-icon>filter_alt</md-icon>
      <span v-if="activeFilterCount" class="ret-dock__badge">{{ activeFilterCount }}</span>
    </button>

    <div v-if="open" class="ret-filter__overlay" @click="open = false"></div>
    <aside class="ret-filter__panel" :class="{ 'is-open': open }">
      <div class="ret-filter__head">
        <h4>{{ $t("returns.filterDrawer.title") }}</h4>
        <md-button class="md-simple md-just-icon md-round" @click="open = false"><md-icon>close</md-icon></md-button>
      </div>

      <div class="ret-filter__body">
        <div v-for="f in fields" :key="f.key" class="ret-filter__section">
          <div class="ret-filter__label">
            <span>{{ $t(f.label) }}</span>
            <!-- Jeder Filter einzeln entfernbar (P20.5); ein Datumspaar zaehlt als ein Filter -->
            <button v-if="isSet(f)" class="ret-filter__clear" :title="$t('returns.filterDrawer.clearOne')" @click="clearOne(f)">
              <md-icon>close</md-icon>
            </button>
          </div>
          <!-- Datumsbereich von/bis (Trello 26.08.2026: "einen Tag filtern") -->
          <div v-if="f.type === 'daterange'" class="ret-filter__dates">
            <date-picker v-model="draft[f.from]" format="YYYY-MM-DD" type="date" :placeholder="$t('returns.filterDrawer.from')" :append-to-body="true" />
            <date-picker v-model="draft[f.to]" format="YYYY-MM-DD" type="date" :placeholder="$t('returns.filterDrawer.to')" :append-to-body="true" />
          </div>
          <v-select
            v-else
            class="custom-select"
            :options="f.options"
            :reduce="(o) => o.id"
            :getOptionLabel="(o) => o.name"
            :value="draft[f.key]"
            :append-to-body="true"
            @input="draft[f.key] = $event"
          />
        </div>
      </div>

      <div class="ret-filter__foot">
        <md-button class="md-secondary md-sm" @click="clearAll">{{ $t("returns.filterDrawer.clearAll") }}</md-button>
        <md-button class="md-success md-sm" @click="apply">{{ $t("returns.filterDrawer.apply") }}</md-button>
      </div>
    </aside>
  </div>
</template>

<script>
// Filter-Drawer der Retouren (P20.4/20.5) nach dem Muster des Lieferplans (DrawerFilter.vue),
// bewusst schlanker: Auswahlfelder, Einzel-x, "Loeschen" und "Anwenden".
import { format } from "date-fns";
import DatePicker from "vue2-datepicker";
import { marketplaceOptions, carrierOptions } from "@/helpers/marketplaces";

const EMPTY = {
  completed: null,
  status: null,
  returnType: null,
  marketplace: null,
  carrier: null,
  createdFrom: null,
  createdTo: null,
  createdById: null,
  completedFrom: null,
  completedTo: null,
  completedById: null,
};
const DATE_KEYS = ["createdFrom", "createdTo", "completedFrom", "completedTo"];

export default {
  name: "ReturnsDrawerFilter",
  components: { DatePicker },
  props: {
    participants: { type: Object, default: () => ({ createdBy: [], completedBy: [] }) },
  },
  data() {
    return {
      open: false,
      draft: { ...EMPTY },
      applied: { ...EMPTY },
    };
  },
  computed: {
    fields() {
      const t = (k) => this.$t(k);
      return [
        {
          key: "completed",
          label: "returns.table.status",
          options: [
            { id: false, name: t("returns.generalStatus.created") },
            { id: true, name: t("returns.generalStatus.completed") },
          ],
        },
        {
          key: "status",
          label: "returns.table.warehouseStatus",
          options: ["captured", "assigned", "checked", "booked"].map((s) => ({ id: s, name: t("returns.statuses." + s) })),
        },
        {
          key: "returnType",
          label: "returns.table.type",
          options: ["customer_return", "undeliverable", "amazon_fba", "other"].map((s) => ({ id: s, name: t("returns.types." + s) })),
        },
        { key: "marketplace", label: "returns.filterDrawer.marketplace", options: marketplaceOptions() },
        { key: "carrier", label: "returns.filterDrawer.carrier", options: carrierOptions() },
        { key: "createdDate", type: "daterange", from: "createdFrom", to: "createdTo", label: "returns.filterDrawer.createdOn" },
        { key: "createdById", label: "returns.filterDrawer.createdBy", options: this.participants.createdBy || [] },
        { key: "completedDate", type: "daterange", from: "completedFrom", to: "completedTo", label: "returns.filterDrawer.completedOn" },
        { key: "completedById", label: "returns.filterDrawer.completedBy", options: this.participants.completedBy || [] },
      ];
    },
    activeFilterCount() {
      let count = Object.entries(this.applied).filter(([k, v]) => !DATE_KEYS.includes(k) && v !== null).length;
      if (this.applied.createdFrom || this.applied.createdTo) count += 1;
      if (this.applied.completedFrom || this.applied.completedTo) count += 1;
      return count;
    },
  },
  watch: {
    // Beim Oeffnen den angewendeten Stand zeigen - kein halbfertiger Entwurf vom letzten Mal;
    // der Parent schliesst dann sein Suchpanel (Ueberlappung, Kundenscreenshot 20.08.)
    open(neu) {
      if (neu) {
        this.draft = { ...this.applied };
        this.$emit("opened");
      } else {
        this.$emit("closed");
      }
    },
  },
  methods: {
    isSet(f) {
      return f.type === "daterange" ? !!(this.draft[f.from] || this.draft[f.to]) : this.draft[f.key] !== null;
    },
    clearOne(f) {
      if (f.type === "daterange") {
        this.draft[f.from] = null;
        this.draft[f.to] = null;
      } else {
        this.draft[f.key] = null;
      }
    },
    apply() {
      this.applied = { ...this.draft };
      this.open = false;
      // Datumswerte als Kalendertag (yyyy-MM-dd) - die API rechnet den Tag in Berliner Zeit um
      const out = { ...this.applied };
      DATE_KEYS.forEach((k) => (out[k] = out[k] ? format(out[k], "yyyy-MM-dd") : null));
      this.$emit("apply-filters", out);
    },
    clearAll() {
      this.draft = { ...EMPTY };
      this.apply();
    },
  },
};
</script>

<style lang="scss" scoped>
.ret-filter__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1049;
}
.ret-filter__panel {
  position: fixed;
  top: 0;
  right: -420px;
  width: 400px;
  max-width: 92vw;
  height: 100vh;
  background: #fff;
  z-index: 1050;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.2);
  transition: right 0.25s ease;
  display: flex;
  flex-direction: column;

  &.is-open {
    right: 0;
  }
}
.ret-filter__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;

  h4 {
    margin: 0;
    font-size: 16px;
  }
}
.ret-filter__body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}
.ret-filter__section {
  margin-bottom: 16px;
}
.ret-filter__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}
.ret-filter__clear {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  .md-icon {
    font-size: 16px !important;
    color: #b71c1c;
  }
}
.ret-filter__dates {
  display: flex;
  gap: 8px;

  ::v-deep .mx-datepicker {
    flex: 1;
    min-width: 0;
    width: auto;
  }
}
.ret-filter__foot {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #eee;
}
</style>
